import { DATE_FORMAT, toAgGridModel } from 'admin/src/components/ui/DataGrid/filters/agGridAdapter';
import { type FilterOperator, operatorValueCount } from 'admin/src/components/ui/DataGrid/filters/operators';
import { PanelOperatorRow } from 'admin/src/components/ui/DataGrid/filters/values/PanelOperatorRow';
import type { ValuePanelProps } from 'admin/src/components/ui/DataGrid/filters/values/ValuePanelProps';
import dayjs from 'dayjs';
import { useRef, useState } from 'react';
import type { DateRange } from 'react-day-picker';
import { Button } from 'shared/src/components/ui/new/Button';
import { Calendar } from 'shared/src/components/ui/new/Calendar';
import { cn } from 'shared/src/utils';

type DateBound = { min: string; max: string };

type Preset = {
  label: string;
  operator: FilterOperator;
  build: () => string[];
};

const PRESETS: Preset[] = [
  { label: 'Today', operator: 'is', build: () => [format(dayjs())] },
  { label: 'Yesterday', operator: 'is', build: () => [format(dayjs().subtract(1, 'day'))] },
  { label: 'Last 7 days', operator: 'is between', build: () => [format(dayjs().subtract(6, 'day')), format(dayjs())] },
  { label: 'Last 30 days', operator: 'is between', build: () => [format(dayjs().subtract(29, 'day')), format(dayjs())] },
  { label: 'This month', operator: 'is between', build: () => [format(dayjs().startOf('month')), format(dayjs().endOf('month'))] },
  { label: 'This year', operator: 'is between', build: () => [format(dayjs().startOf('year')), format(dayjs().endOf('year'))] },
];

export function DateValue({ column, operator, values, range, showOperator = true, onChange }: ValuePanelProps & { range?: DateBound; showOperator?: boolean }) {
  const [draftOperator, setDraftOperator] = useState(operator);
  const [draft, setDraft] = useState(values);

  const propsKey = `${operator}\u0000${values.join('\u0000')}`;
  const lastPropsKey = useRef(propsKey);
  if (lastPropsKey.current !== propsKey) {
    lastPropsKey.current = propsKey;
    setDraftOperator(operator);
    setDraft(values);
  }

  const isRange = operatorValueCount(column.dataType, draftOperator) === 2;

  function apply(nextOperator: FilterOperator, nextValues: string[]) {
    setDraftOperator(nextOperator);
    setDraft(nextValues);

    if (toAgGridModel(column.dataType, nextOperator, nextValues)) {
      onChange(nextOperator, nextValues);
    }
  }

  const anchor = draft[0] ? parse(draft[0]) : new Date();
  const activePreset = PRESETS.find((preset) => preset.operator === draftOperator && sameValues(preset.build(), draft));

  return (
    <div>
      {showOperator ? <PanelOperatorRow column={column} operator={draftOperator} values={draft} fallbackSecondValue={range?.max} onChange={apply} /> : null}
      <div className="grid grid-cols-2 gap-1 p-1.5">
        {PRESETS.map((preset) => (
          <Button
            key={preset.label}
            variant="ghost"
            size="sm"
            className={cn('h-6 justify-start px-1.5 text-xs font-normal', activePreset === preset && 'bg-accent text-accent-foreground')}
            onClick={() => apply(preset.operator, preset.build())}
          >
            {preset.label}
          </Button>
        ))}
      </div>
      {isRange ? (
        <Calendar
          mode="range"
          defaultMonth={anchor}
          className="border-0 p-2"
          selected={{ from: draft[0] ? parse(draft[0]) : undefined, to: draft[1] ? parse(draft[1]) : undefined }}
          onSelect={(rangeSelection: DateRange | undefined) => {
            const next = [rangeSelection?.from, rangeSelection?.to].filter(Boolean).map((date) => format(dayjs(date)));
            apply(draftOperator, next);
          }}
        />
      ) : (
        <Calendar
          mode="single"
          defaultMonth={anchor}
          className="border-0 p-2"
          selected={draft[0] ? parse(draft[0]) : undefined}
          onSelect={(date) => {
            if (!date) return;
            apply(draftOperator, [format(dayjs(date))]);
          }}
        />
      )}
    </div>
  );
}

function format(date: dayjs.Dayjs) {
  return date.format(DATE_FORMAT);
}

function parse(value: string) {
  return dayjs(value).toDate();
}

function sameValues(a: string[], b: string[]) {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}
